/** QuickPlan — Assistant : fil de discussion du projet, tour en direct (texte, outils,
 *  statut), palier rapide / puissant, arrêt. Pendant un tour, l'édition est verrouillée. */
import { useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { prepareImage, prepareDocument, droppedFiles, isImage, docIcon, formatSize, MAX_IMAGES, MAX_DOCS, MAX_TOTAL_BYTES, ACCEPT } from '../images.js';
import MiniMarkdown from './MiniMarkdown.jsx';

const EXAMPLES = [
  'Un établi de 1500×750 en profilé 45×45, plateau de 30 mm, avec une étagère basse.',
  'Une baie 42U 800×1000 avec 3 serveurs 2U en bas, un onduleur et 2 switchs 48 ports.',
  'Un meuble bas de 1200 de large avec deux portes et deux tiroirs à droite.',
  'Remplace tous les profilés 40×40 par du 45×45 et ajuste les longueurs.',
];

const TOOL_SHORT = {
  qpl_get_project: 'lecture', qpl_search_library: 'bibliothèque', qpl_get_component: 'composant', qpl_ports: 'ports',
  qpl_add_instances: 'pose', qpl_update_instances: 'modification', qpl_delete_instances: 'suppression',
  qpl_insert_template: 'assemblage type', qpl_groups: 'sous-ensembles', qpl_define_component: 'nouveau modèle',
  qpl_get_bom: 'nomenclature', qpl_set_extras: 'lignes libres', qpl_open_all: 'ouvrir / fermer', qpl_attachments: 'pièce jointe', ToolSearch: 'outils',
};

function Thumbs({ srcs }) {
  if (!srcs?.length) return null;
  return (
    <div className="msg-images">
      {srcs.map((src) => <a key={src} href={src} target="_blank" rel="noreferrer" title="Ouvrir l'image"><img src={src} alt="image jointe" /></a>)}
    </div>
  );
}

function DocChips({ files, href }) {
  if (!files?.length) return null;
  return (
    <div className="msg-docs">
      {files.map((f) => (
        <a key={f.file ?? f.name} className="doc-chip" href={href?.(f)} target="_blank" rel="noreferrer" title={f.file ? 'Ouvrir le document' : f.name}>
          <span>{docIcon(f.name)}</span><span className="doc-name">{f.name}</span>{f.size ? <span className="muted">{formatSize(f.size)}</span> : null}
        </a>
      ))}
    </div>
  );
}

function ToolChips({ tools }) {
  if (!tools?.length) return null;
  const counts = new Map();
  for (const t of tools) counts.set(t, (counts.get(t) ?? 0) + 1);
  return (
    <div className="tool-chips">
      {[...counts.entries()].map(([t, n]) => <span key={t} className="chip">{TOOL_SHORT[t] ?? t}{n > 1 ? ` ×${n}` : ''}</span>)}
    </div>
  );
}

export default function ChatPanel() {
  const ai = useProjectStore((s) => s.ai);
  const send = useProjectStore((s) => s.sendPrompt);
  const cancel = useProjectStore((s) => s.cancelJob);
  const setTier = useProjectStore((s) => s.setTier);
  const clearChat = useProjectStore((s) => s.clearChat);
  const selection = useProjectStore((s) => s.selection);
  const readOnly = useProjectStore((s) => s.project?.role === 'viewer');
  const projectId = useProjectStore((s) => s.project?.id);
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [docs, setDocs] = useState([]);
  const [imgError, setImgError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const listRef = useRef(null);
  const fileRef = useRef(null);
  const locked = !!ai.job || readOnly;

  const addFiles = async (files) => {
    setImgError('');
    if (!files.length) return;
    const pics = files.filter(isImage);
    const others = files.filter((f) => !isImage(f));
    const errors = [];
    const room = MAX_IMAGES - images.length;
    const okImg = [];
    for (const f of pics.slice(0, Math.max(0, room))) {
      try { okImg.push(await prepareImage(f)); } catch (e) { errors.push(e.message); }
    }
    if (pics.length > room) errors.push(`${MAX_IMAGES} images au plus par demande : ${pics.length - Math.max(0, room)} ignorée(s)`);
    const docRoom = MAX_DOCS - docs.length;
    let total = docs.reduce((n, d) => n + d.bytes, 0) + images.reduce((n, im) => n + im.data.length * 0.75, 0);
    const okDoc = [];
    for (const f of others.slice(0, Math.max(0, docRoom))) {
      try {
        const d = await prepareDocument(f);
        if (total + d.bytes > MAX_TOTAL_BYTES) throw new Error(`« ${f.name} » ignoré : ${MAX_TOTAL_BYTES / 1048576} Mo de pièces jointes au plus par demande`);
        total += d.bytes;
        okDoc.push(d);
      } catch (e) { errors.push(e.message); }
    }
    if (others.length > docRoom) errors.push(`${MAX_DOCS} documents au plus par demande : ${others.length - Math.max(0, docRoom)} ignoré(s)`);
    setImgError(errors.join(' · '));
    setImages((cur) => [...cur, ...okImg]);
    setDocs((cur) => [...cur, ...okDoc]);
  };
  const removeImage = (id) => setImages((cur) => {
    const gone = cur.find((x) => x.id === id);
    if (gone) URL.revokeObjectURL(gone.preview);
    return cur.filter((x) => x.id !== id);
  });

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ai.messages, ai.text, ai.job]);

  const hasAttach = images.length > 0 || docs.length > 0;
  const submit = () => {
    if ((!text.trim() && !hasAttach) || ai.job) return;
    const prompt = text.trim() || (docs.length
      ? 'Voici un document : analyse-le et propose ce que tu peux en tirer pour le projet.'
      : 'Voici une image : analyse-la et propose ce que tu peux en tirer pour le projet.');
    send(prompt, images, docs);
    setText('');
    setImages([]);
    setDocs([]);
    setImgError('');
  };
  const imgUrl = (file) => `/api/projects/${projectId}/chat/images/${file}`;
  const docUrl = (f) => `/api/projects/${projectId}/chat/files/${f.file}?name=${encodeURIComponent(f.name)}`;

  if (ai.available === false) {
    return (
      <div className="panel-body">
        <div className="empty">L'assistant n'est pas disponible sur ce serveur : le CLI <code>claude</code> est introuvable (variable <code>QUICKPLAN_CLAUDE_PATH</code>).</div>
      </div>
    );
  }

  return (
    <div
      className={`chat ${dragOver ? 'drag-over' : ''}`}
      onDragOver={(e) => { if (!locked && [...e.dataTransfer.types].includes('Files')) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!locked) addFiles(droppedFiles(e.dataTransfer.files)); }}
    >
      <div className="chat-list" ref={listRef}>
        {!ai.messages.length && !ai.job && (
          <div className="chat-intro">
            <p>Décrivez ce que vous voulez construire ou modifier : l'assistant pose les composants, les range en sous-ensembles, crée les modèles manquants et prépare la nomenclature.</p>
            {EXAMPLES.map((e) => <button key={e} className="example" onClick={() => setText(e)}>{e}</button>)}
          </div>
        )}
        {ai.messages.map((m, k) => (
          <div key={k} className={`msg ${m.role} ${m.error ? 'error' : ''}`}>
            {m.role === 'user' && m.by && <div className="msg-by">{m.by}</div>}
            <Thumbs srcs={m.images?.map(imgUrl)} />
            <DocChips files={m.files} href={docUrl} />
            {m.role === 'assistant' ? <MiniMarkdown text={m.content} /> : <div className="msg-text">{m.content}</div>}
            {m.role === 'assistant' && <ToolChips tools={m.tools} />}
            {m.role === 'assistant' && m.durationMs != null && (
              <div className="msg-meta">{Math.round(m.durationMs / 1000)} s{m.costUsd ? ` · ${m.costUsd.toFixed(3)} $` : ''}</div>
            )}
          </div>
        ))}
        {ai.job && (
          <>
            {ai.pending && <div className="msg user"><Thumbs srcs={ai.pendingImages} /><DocChips files={ai.pendingDocs} /><div className="msg-text">{ai.pending}</div></div>}
            <div className="msg assistant live">
              {ai.text && <MiniMarkdown text={ai.text} />}
              <ToolChips tools={ai.tools} />
              <div className="msg-status"><span className="spinner" /> {ai.status || 'Travail en cours…'}</div>
            </div>
          </>
        )}
        {ai.error && <div className="msg assistant error"><div className="msg-text">⚠ {ai.error}</div></div>}
      </div>
      <div className="chat-input">
        {hasAttach && (
          <div className="attach-row">
            {docs.map((d) => (
              <div key={d.id} className="attach doc" title={`${d.name} — ${formatSize(d.bytes)}`}>
                <span className="doc-icon">{docIcon(d.name)}</span>
                <span className="doc-name">{d.name}</span>
                <button className="attach-x" title="Retirer" onClick={() => setDocs((cur) => cur.filter((x) => x.id !== d.id))}>✕</button>
              </div>
            ))}
            {images.map((im) => (
              <div key={im.id} className="attach" title={`${im.name} — ${im.size[0]}×${im.size[1]}`}>
                <img src={im.preview} alt={im.name} />
                <button className="attach-x" title="Retirer" onClick={() => removeImage(im.id)}>✕</button>
              </div>
            ))}
          </div>
        )}
        {imgError && <div className="warn small">{imgError}</div>}
        <textarea
          rows={3}
          value={text}
          placeholder={readOnly ? 'Projet en lecture seule : l\'assistant ne peut pas le modifier.' : ai.job ? 'L\'assistant travaille… (édition verrouillée)' : `Votre demande${selection.length ? ` (sélection : ${selection.length} élément${selection.length > 1 ? 's' : ''})` : ''} — 📎 ou glisser-déposer pour joindre images, PDF, Word, Excel…, Ctrl+Entrée pour envoyer`}
          disabled={!!ai.job || readOnly}
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => {
            const files = droppedFiles(e.clipboardData?.items);
            if (files.length) { e.preventDefault(); addFiles(files); }
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
          }}
        />
        {dragOver && <div className="drop-hint">Déposez images ou documents (PDF, Word, Excel…) pour les joindre à la demande</div>}
        <div className="row">
          <div className="seg">
            <button className={ai.tier === 'fast' ? 'active' : ''} onClick={() => setTier('fast')} title="Modèle rapide">Rapide</button>
            <button className={ai.tier === 'strong' ? 'active' : ''} onClick={() => setTier('strong')} title="Modèle le plus capable (plus lent)">Puissant</button>
          </div>
          <button className="icon" disabled={locked} title={`Joindre des images (${MAX_IMAGES} au plus) ou des documents — PDF, Word, Excel, PowerPoint, OpenDocument, CSV, texte (${MAX_DOCS} au plus) — ou collez une capture (Ctrl+V), ou glissez-les ici`} onClick={() => fileRef.current?.click()}>📎</button>
          <input ref={fileRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { const f = [...(e.target.files ?? [])]; e.target.value = ''; addFiles(f); }} />
          <span className="grow" />
          {ai.messages.length > 0 && !ai.job && <button className="icon" title="Effacer le fil" onClick={() => window.confirm('Effacer tout le fil de discussion de ce projet ?') && clearChat()}>🗑</button>}
          {ai.job
            ? <button className="danger" onClick={cancel}>■ Arrêter</button>
            : <button className="primary" disabled={(!text.trim() && !hasAttach) || readOnly} onClick={submit}>Envoyer</button>}
        </div>
      </div>
    </div>
  );
}
